import { useState } from 'react'
import { Page, PageHeader } from '@/components/page'
import { router } from '@inertiajs/react'
import { WizardGuide, GuideTerm } from '@/components/layout/WizardGuide'
import { WizardLayout } from '../WizardLayout'
import { WizardActions } from '../WizardActions'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { firstError } from '@/lib/utils'
import { useBasePath } from '@/lib/paths'
import { RecommendedState, WizardFormationRef, WizardStateOption } from '@/types'
import { ArrowRight, Clock, EyeOff, ListFilter } from 'lucide-react'
import { Tip } from '@/components/sketch/Tip'

type Props = {
  formation: WizardFormationRef
  current_state: { id: string; code: string; name: string } | null
  recommendations: RecommendedState[]
  states: WizardStateOption[]
}

function reasonSentence(recommended: RecommendedState) {
  const { reason } = recommended
  const perks: string[] = []
  if (reason.allows_anonymous_ownership) perks.push('keeps your ownership private')
  if (!reason.requires_public_member_disclosure) perks.push("doesn't require public member disclosure")
  const perkText = perks.length ? `${perks.join(' and ')}, and ` : ''
  const days = reason.processing_days_min != null && reason.processing_days_max != null
    ? `our filing estimate is ${reason.processing_days_min}-${reason.processing_days_max} business days`
    : 'the processing estimate is not published yet'
  return `${perkText}${days}.`
}

export default function StatesEdit({ formation, current_state, recommendations, states }: Props) {
  const basePath = useBasePath()
  const [selectedStateCode, setSelectedStateCode] = useState(current_state?.code ?? '')
  const recommendationCodes = recommendations.map((recommendation) => recommendation.state.code)
  const [showAll, setShowAll] = useState(Boolean(current_state && !recommendationCodes.includes(current_state.code)))
  const [stateSearch, setStateSearch] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const filteredStates = states.filter((option) => option.state.name.toLowerCase().includes(stateSearch.trim().toLowerCase()))

  const submit = () => {
    if (submitting || !selectedStateCode) return
    setSubmitting(true)
    setError(null)
    router.patch(`${basePath}/formations/${formation.id}/wizard/state`, { state_code: selectedStateCode }, {
      onError: (errors) => setError(firstError(errors.state_code) ?? 'Something went wrong — try again.'),
      onFinish: () => setSubmitting(false),
    })
  }

  return (
    <WizardLayout
      title="Choose a state"
      currentStep="state"
      formationId={formation.id}
      resumeStep={formation.current_wizard_step}
      breadcrumbs={[{ label: 'Businesses', href: 'businesses' }, { label: 'Choose a state' }]}
      guide={
        <WizardGuide title="How states differ">
          <p>The state you file in changes your price, your privacy, and how fast we can file.</p>
          <GuideTerm term="Price">You’ll see the exact LLC and C-Corp totals when you choose your structure next.</GuideTerm>
          <GuideTerm term="Privacy">Some states keep your ownership private; others require public member disclosure.</GuideTerm>
          <GuideTerm term="Speed">Processing times vary by state — the estimate is listed alongside each one.</GuideTerm>
        </WizardGuide>
      }
      tips={
        <Tip seed="states-take-our-pick">
          Most people take our pick and move on — you can browse the rest after.
        </Tip>
      }
    >
      <Page>
        <PageHeader
          title={<>Where should we file?</>}
          subtitle={<>The state affects privacy and speed. You’ll choose LLC or C-Corp next.</>}
        />

        <form onSubmit={(e) => { e.preventDefault(); submit() }}>
          <fieldset className="space-y-4">
            <legend className="text-sm font-medium text-foreground">Choose a state</legend>

            {!showAll && recommendations.length > 0 && (
              <RadioGroup value={selectedStateCode} onValueChange={setSelectedStateCode} className="grid gap-3">
                {recommendations.map((recommended, index) => (
                  <label
                    key={recommended.state.id}
                    htmlFor={`state-${recommended.state.code}`}
                    className="group flex items-start gap-3 rounded-lg border p-5 cursor-pointer transition-colors has-data-[state=checked]:border-foreground has-data-[state=checked]:bg-muted hover:border-foreground/40"
                  >
                    <RadioGroupItem
                      value={recommended.state.code}
                      id={`state-${recommended.state.code}`}
                      className="mt-1"
                      disabled={submitting}
                    />
                    <span className="flex flex-col gap-1">
                      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        {index === 0 ? 'Our pick for you' : `Recommended #${index + 1}`}
                      </span>
                      <span className="text-lg font-medium text-foreground">{recommended.state.name}</span>
                      <span className="text-sm text-muted-foreground">{reasonSentence(recommended)}</span>
                    </span>
                  </label>
                ))}
              </RadioGroup>
            )}

            {!showAll ? (
              <Button type="button" variant="ghost" size="sm" onClick={() => setShowAll(true)} className="text-muted-foreground">
                <ListFilter className="size-3.5" />
                See all supported states
              </Button>
            ) : (
              <div className="space-y-3">
                <label htmlFor="state-search" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Supported states</label>
                <Input
                  id="state-search"
                  type="search"
                  value={stateSearch}
                  onChange={(event) => setStateSearch(event.target.value)}
                  placeholder="Search states…"
                />
                <RadioGroup value={selectedStateCode} onValueChange={setSelectedStateCode} className="gap-0">
                  <ul className="divide-y rounded-lg border">
                    {filteredStates.map((option) => (
                      <li key={option.state.id}>
                        <label
                          htmlFor={`all-state-${option.state.code}`}
                          className="group flex w-full items-center gap-3 px-4 py-3 cursor-pointer transition-colors has-data-[state=checked]:bg-muted/50 hover:bg-muted/50"
                        >
                          <RadioGroupItem value={option.state.code} id={`all-state-${option.state.code}`} disabled={submitting} />
                          <span className="flex flex-1 items-center justify-between gap-4 min-w-0">
                            <span className="flex items-center gap-2 min-w-0">
                              <span className="text-sm font-medium text-foreground truncate">{option.state.name}</span>
                              {option.allows_anonymous_ownership && (
                                <span className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                                  <EyeOff className="size-3" /> private
                                </span>
                              )}
                            </span>
                            <span className="flex items-center text-xs text-muted-foreground shrink-0">
                              {option.processing_days_min != null && option.processing_days_max != null && (
                                <span className="flex items-center gap-1">
                                  <Clock className="size-3" />
                                  {option.processing_days_min}-{option.processing_days_max}d
                                </span>
                              )}
                            </span>
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </RadioGroup>
                {filteredStates.length === 0 && <p className="text-sm text-muted-foreground">No states found.</p>}
              </div>
            )}
          </fieldset>

          {error && <p className="mt-3 text-sm text-destructive" role="alert">{error}</p>}

          <WizardActions currentStep="state" formationId={formation.id} disabled={submitting}>
            <Button type="submit" disabled={submitting || !selectedStateCode}>
              {submitting ? 'Saving…' : 'Continue'}
              <ArrowRight className="size-4" />
            </Button>
          </WizardActions>
        </form>
      </Page>
    </WizardLayout>
  )
}
